import {Barcode, Box, Platform} from "@variocube/app-api";
import {EventSupportingApp} from "@variocube/app-common";
import {
    AppContext,
    BoxAccess,
    classNames,
    CodeDigits0,
    CodeForm,
    layout,
    ThemeProvider,
    typography,
    useBarcodeListener,
    useBoxes,
    usePlatform,
    useStorageItemValue,
    View
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useState} from "react";
import {style} from "typestyle";
import {defaultCodes} from "./defaultCodes";
import icon from "./icon-lottery.svg";
import {render} from "react-dom";
import {LocalizationProvider, SUPPORTED_LANGUAGES, useLocalization} from "../i18n";

export const ConfigKeyCodes = "codes";
export const ConfigKeyBoxes = "boxes";
export const ConfigKeyWeighting = "weighting";
export const ConfigKeyGrandPrizes = "grandPrizes";

const CodeLength = 5;

type Weighting = { [boxNumber: string]: number };

export default class LotteryApp extends EventSupportingApp {

    constructor(platform: Platform) {
        super(platform, icon, "#166a31", SUPPORTED_LANGUAGES);
    }

    render(appRoot: Element) {
        const appContext = {
            platform: this.platform,
            appEventSupport: this.eventSupport
        };
        render((
            <AppContext.Provider value={appContext}>
                <ThemeProvider defaultTheme={themeOptions}>
                    <LocalizationProvider app={this}>
                        <Lottery />
                    </LocalizationProvider>
                </ThemeProvider>
            </AppContext.Provider>
        ), appRoot);
    }

    loadMockData() {
        return {
            [ConfigKeyWeighting]: {
                "1": 5,
                "2": 10,
            },
            [ConfigKeyBoxes]: ["1", "2"],
            [ConfigKeyGrandPrizes]: {
                "12345": "3"
            },
        };
    }
}

const themeOptions = {
    colorBackground: "#d7f5e1",
    colorPrimary: "#166a31",
    colorForeground: "#333",
    colorInputBackground: "#fff"
};

const usedCodes: string[] = [];

function Lottery() {
    const {t} = useLocalization();
    const [winningBox, setWinningBox] = useState<Box>();
    const platform = usePlatform();
    const codes = useStorageItemValue<string[]>(ConfigKeyCodes) || defaultCodes;
    const grandPrizes = useStorageItemValue<Record<string, string>>(ConfigKeyGrandPrizes) || {};
    const weighting = useStorageItemValue<Weighting>(ConfigKeyWeighting) || {}
    const allBoxes = useBoxes();
    const configuredBoxes = useStorageItemValue<string[]>(ConfigKeyBoxes);


    async function getPrize(code: string) {

        console.log("codes", codes);
        console.log("grandPrizes", grandPrizes);

        if (usedCodes.indexOf(code) >= 0) {
            throw t("usedCode");
        }

        const grandPrize = grandPrizes[code];

        if (!grandPrize && codes.indexOf(code) < 0) {
            throw t("invalidCode");
        }

        usedCodes.push(code);

        if (grandPrize) {
            return allBoxes.find(box => box.number == grandPrize);
        }
        else {
            return computeWinningBox();
        }
    }

    function getLotteryBoxes() {
        if (configuredBoxes) {
            return allBoxes.filter(box => configuredBoxes.includes(box.number));
        }
        else {
            const filtered = allBoxes.filter((b) =>
                b.types.filter((type) => type === "Lottery").length > 0
            );
            return filtered.length > 0 ? filtered : allBoxes;
        }
    }

    async function computeWinningBox() {
        const boxes = getLotteryBoxes();

        const getWeighting = (box: Box) => weighting[box.number] || 1;

        let sumWeight = 0;
        for (let box of boxes) {
            sumWeight += getWeighting(box);
        }

        const diceThrow = sumWeight * Math.random();
        let lower = 0;
        for (let box of boxes) {
            let upper = lower + getWeighting(box);
            if (diceThrow >= lower && diceThrow < upper) {
                return box;
            }
            lower = upper;
        }

        throw t("internalError");
    }

    async function handleVerify(code: string){
        setWinningBox(await getPrize(code));
    }

    const handleBarcode = useCallback(async (code: Barcode) => {
        await handleVerify(atob(code.code));
    }, [handleVerify]);
    useBarcodeListener(handleBarcode);

    const handleConfirm = useCallback(() => setWinningBox(undefined), []);

    if (winningBox) {
        return (
            <BoxAccess
                boxes={winningBox}
                platform={platform}
                onCancel={handleConfirm}
                onConfirm={handleConfirm}
                timeout
                boxOpenTitle={t("boxOpenTitle")}
                boxOpenMessage={t("boxOpenMessage")}
                confirmTitle={t("boxConfirmTitle")}
                confirmMessage={t("boxConfirmMessage")}
                strings={t}
            />
        );
    }
    else {
        return (
            <View className={layout.center}>
                <div className={classNames(typography.alignCenter, style({padding: "1em"}))}>
                    <p>{t("enterCodeMessage")}</p>
                </div>
                <CodeForm
                    onVerify={handleVerify}
                    strings={t}
                    length={CodeLength}
                    input={{type: "keypad", digits: CodeDigits0}}
                />
            </View>
        );
    }
}
